// Extracted from App.tsx (ocn-S184-P0). Behavior-preserving move.
import { useState, useEffect, useCallback } from "react";
import { Activity, AlertTriangle, CheckCircle, Code2, Lock, Package } from "lucide-react";
import api from "../../../api";
import { timeAgo } from "../../../lib/helpers";

export function RecentJournal({ projectId }: { projectId: string }) {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const fetchEntries = useCallback(async (): Promise<boolean> => {
    try {
      const data = await (api as any).journal?.list(projectId, 20);
      setEntries(data);
      return true;
    } catch (error) {
      // Keep the last successful journal visible. Network/CORS errors are often
      // transient immediately after a failed stream handshake.
      console.debug('[journal] fetch failed; retaining last entries', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let delay = 30_000;
    setLoading(true);

    const poll = async () => {
      const succeeded = await fetchEntries();
      if (cancelled) return;
      delay = succeeded ? 30_000 : Math.min(delay * 2, 120_000);
      timer = setTimeout(() => { void poll(); }, delay);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [fetchEntries]);

  const getEventIcon = (eventType: string) => {
    if (eventType.includes('completed') || eventType.includes('resolved')) return <CheckCircle size={12} style={{ color: 'var(--green)' }} />;
    if (eventType.includes('failed') || eventType.includes('error')) return <AlertTriangle size={12} style={{ color: 'var(--red)' }} />;
    if (eventType.includes('pipeline') || eventType.includes('review')) return <Code2 size={12} style={{ color: 'var(--blue)' }} />;
    if (eventType.includes('module') || eventType.includes('finding')) return <Package size={12} style={{ color: 'var(--purple, #a78bfa)' }} />;
    if (eventType.includes('credential')) return <Lock size={12} style={{ color: 'var(--orange, #f59e0b)' }} />;
    return <Activity size={12} style={{ color: 'var(--text-dim)' }} />;
  };

  if (loading) return <div className="card" style={{ padding: "12px 14px" }}><div className="spinner" style={{ width: 12, height: 12 }} /></div>;
  if (entries.length === 0) return (
    <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius-lg)", marginBottom: 20 }}>
      <span className="muted" style={{ fontSize: "var(--text-sm)" }}>No journal entries yet.</span>
    </div>
  );

  const visible = showAll ? entries : entries.slice(0, 10);
  const syncFailure = (entry: any) => {
    const message = entry.event_type === 'sync_failed' ? entry.metadata?.sync_failure : null;
    return message && typeof message.title === 'string' && typeof message.body === 'string'
      ? message
      : entry.event_type === 'sync_failed'
        ? { title: 'Sync could not complete', body: 'Sync couldn’t complete. Our team has been notified.', category: 'platform', actionable: false }
        : null;
  };

  return (
    <div className="card" style={{ padding: 0, marginBottom: 20 }}>
      {visible.map((e: any, i: number) => {
        const failure = syncFailure(e);
        return (
          <div key={e.id} className={failure ? `journal-sync-failure journal-sync-failure--${failure.category === 'subscriber' ? 'subscriber' : 'platform'}` : undefined} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: i < visible.length - 1 ? "1px solid var(--border)" : "none" }}>
            {failure?.category === 'platform' ? <Activity size={12} className="muted" /> : getEventIcon(e.event_type)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {failure?.title || e.title || e.issue_title || e.summary?.substring(0, 80)}
              </div>
              {failure && <div className="journal-sync-failure__body">{failure.body}</div>}
              {failure?.actionable && <div className="journal-sync-failure__action">Action needed: update the project, then run Sync again.</div>}
              <div className="mono muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>
                {e.metadata?.pr_number ? `PR #${e.metadata.pr_number} \u00B7 ` : e.pr_number ? `PR #${e.pr_number} \u00B7 ` : ""}
                {e.created_at ? timeAgo(e.created_at) : ""}
              </div>
            </div>
            {(e.metadata?.areas || e.areas)?.slice(0, 2).map((a: string, j: number) => (
              <span key={j} className="badge" style={{ fontSize: 10 }}>{a}</span>
            ))}
          </div>
        );
      })}
      {entries.length > 10 && !showAll && (
        <div style={{ padding: "8px 14px", textAlign: "center", borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-sm" style={{ fontSize: 'var(--text-xs)' }} onClick={() => setShowAll(true)}>
            View all {entries.length} entries &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
